import React from 'react';
import { Calendar, MapPin, Users, Activity } from 'lucide-react';
import { companyInfo, aboutStats, technicalSpecs } from '../data/mock';

const About = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About <span className="text-cyan-400">HVGTEC</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Leading the way in carrier-grade ISP solutions and network infrastructure across the Philippines
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {aboutStats.map((stat, index) => (
              <div key={index} className="text-center bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300">
                <div className="flex justify-center mb-4">
                  {stat.icon === 'Calendar' && <Calendar className="text-cyan-400" size={40} />}
                  {stat.icon === 'MapPin' && <MapPin className="text-cyan-400" size={40} />}
                  {stat.icon === 'Users' && <Users className="text-cyan-400" size={40} />}
                  {stat.icon === 'Activity' && <Activity className="text-cyan-400" size={40} />}
                </div>
                <div className="text-4xl md:text-5xl font-bold text-cyan-400 mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  Founded in Southern Leyte, HVGTEC emerged from a simple mission: to bridge the digital divide in regions where reliable internet connectivity seemed impossible.
                </p>
                <p>
                  Starting with a single solar-powered Micro POP serving a remote barangay, we've grown into a comprehensive ISP and systems integration firm serving over 45,000 clients across the Philippines.
                </p>
                <p>
                  Our expertise in deploying carrier-grade networks using Ubiquiti, MikroTik, and Juniper equipment, combined with our innovative solar-powered solutions, has made us the go-to partner for communities in typhoon-prone and off-grid areas.
                </p>
                <p>
                  Today, we offer scalable franchise models—from 120-client Micro POPs to 4,000+ client Regional Cores—each designed with proven ROI projections and complete technical support.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9"
                alt="Solar Infrastructure"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/20 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-10 hover:border-cyan-500/50 transition-all duration-300">
              <h3 className="text-3xl font-bold text-white mb-4">Our Mission</h3>
              <p className="text-gray-400 leading-relaxed">
                To provide accessible, reliable, and carrier-grade internet infrastructure to underserved communities across the Philippines, utilizing innovative solar-powered technology and proven network architectures that ensure connectivity even in the most challenging environments.
              </p>
            </div>
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-10 hover:border-cyan-500/50 transition-all duration-300">
              <h3 className="text-3xl font-bold text-white mb-4">Our Vision</h3>
              <p className="text-gray-400 leading-relaxed">
                To become the leading ISP infrastructure provider in the Philippines, known for bridging the digital divide through resilient, sustainable, and scalable network solutions that empower communities and enable economic growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Capabilities */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Technical Excellence
            </h2>
            <p className="text-xl text-gray-400">
              Carrier-grade infrastructure and industry certifications
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Protocols</h3>
              <ul className="space-y-2">
                {technicalSpecs.protocols.map((protocol, index) => (
                  <li key={index} className="text-cyan-400 text-sm">• {protocol}</li>
                ))}
              </ul>
            </div>

            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Vendors</h3>
              <ul className="space-y-2">
                {technicalSpecs.vendors.map((vendor, index) => (
                  <li key={index} className="text-cyan-400 text-sm">• {vendor}</li>
                ))}
              </ul>
            </div>

            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Certifications</h3>
              <ul className="space-y-2">
                {technicalSpecs.certifications.networking.slice(0, 2).map((cert, index) => (
                  <li key={index} className="text-cyan-400 text-sm">• {cert}</li>
                ))}
                {technicalSpecs.certifications.mikrotik.slice(0, 2).map((cert, index) => (
                  <li key={`mt-${index}`} className="text-cyan-400 text-sm">• {cert}</li>
                ))}
                {technicalSpecs.certifications.security.slice(0, 2).map((cert, index) => (
                  <li key={`sec-${index}`} className="text-cyan-400 text-sm">• {cert}</li>
                ))}
              </ul>
            </div>

            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Capabilities</h3>
              <ul className="space-y-2">
                {technicalSpecs.capabilities.slice(0, 6).map((capability, index) => (
                  <li key={index} className="text-cyan-400 text-sm">• {capability}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-cyan-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🎯</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Reliability</h3>
              <p className="text-gray-400">
                99.9% uptime guarantee backed by carrier-grade equipment and redundant systems
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-cyan-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">⚡</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Innovation</h3>
              <p className="text-gray-400">
                Solar-powered infrastructure and cutting-edge network protocols for challenging environments
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-cyan-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🤝</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Partnership</h3>
              <p className="text-gray-400">
                Complete support from deployment to operation with proven franchise models
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
